import React, { useState, useMemo } from 'react';
import { CheckCircle } from 'lucide-react';
import AnimatedProgressBar from '../components/AnimatedProgressBar';
import { useSiteTranslation } from '../hooks/useSiteTranslation';
import { useLandingSections } from '../hooks/useLandingSections';
import EditableSection from '../components/Cms/EditableSection';
import { resolveIcon } from '../utils/iconCatalog';
import type { ResolvedSectionContent } from '../i18n/localizeContent';
import { Button } from '../components/UI';

const COUNTRY_KEYS = ['canada', 'australia', 'portugal', 'usa'] as const;
const COUNTRY_FLAGS: Record<(typeof COUNTRY_KEYS)[number], string> = {
  canada: '🇨🇦',
  australia: '🇦🇺',
  portugal: '🇵🇹',
  usa: '🇺🇸',
};

const Immigration: React.FC = () => {
  const { ts, t } = useSiteTranslation();
  const { sections, refetch } = useLandingSections('immigration');
  const [activeTab, setActiveTab] = useState('');

  const heroFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: ts('immigration.title1'), title2: ts('immigration.title2'), subtitle: t('immigration.subtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: []
    }),
    [ts, t]
  );

  const typesFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: t('immigration.typesTitle'), title2: '', subtitle: t('immigration.typesSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: [
        { id: 'work', icon: 'Briefcase', title: t('immigration.workVisa'), description: t('immigration.workVisaDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] },
        { id: 'study', icon: 'GraduationCap', title: t('immigration.studyVisa'), description: t('immigration.studyVisaDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] },
        { id: 'family', icon: 'Heart', title: t('immigration.family'), description: t('immigration.familyDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] },
        { id: 'investment', icon: 'DollarSign', title: t('immigration.investment'), description: t('immigration.investmentDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] }
      ]
    }),
    [t]
  );

  const countriesFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: t('immigration.countriesTitle'), title2: '', subtitle: t('immigration.countriesSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: COUNTRY_KEYS.map((key, index) => ({
        id: String(index),
        flag: COUNTRY_FLAGS[key],
        title: t(`immigration.countries.${key}.name`),
        description: t(`immigration.countries.${key}.description`),
        label: '', duration: '',
        features: [],
        popularVisas: t(`immigration.countries.${key}.visas`, { returnObjects: true }) as string[],
        requirements: t(`immigration.countries.${key}.requirements`, { returnObjects: true }) as string[]
      }))
    }),
    [t]
  );

  const processFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: t('immigration.processTitle'), title2: '', subtitle: t('immigration.processSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: [1, 2, 3, 4, 5, 6].map((n, index) => ({
        id: String(index),
        icon: ['Users', 'FileText', 'FileText', 'Globe', 'Clock', 'CheckCircle'][index],
        title: t(`immigration.steps.${n}.title`),
        description: t(`immigration.steps.${n}.description`),
        duration: t(`immigration.steps.${n}.duration`),
        label: '', features: [], popularVisas: [], requirements: []
      }))
    }),
    [t]
  );

  const servicesFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: t('immigration.servicesTitle'), title2: '', subtitle: t('immigration.servicesSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: (['consulting', 'documents', 'followUp'] as const).map((key, index) => ({
        id: String(index),
        title: t(`immigration.servicePackages.${key}.title`),
        description: t(`immigration.servicePackages.${key}.description`),
        features: t(`immigration.servicePackages.${key}.features`, { returnObjects: true }) as string[],
        label: '', duration: '', popularVisas: [], requirements: []
      }))
    }),
    [t]
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient section-padding">
        <div className="container-custom">
          <EditableSection sectionKey="immigration.hero" data={sections['immigration.hero']} fallback={heroFallback} onSaved={refetch}>
            {(content) => (
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  {content.title}{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
                    {content.title2}
                  </span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">{content.subtitle}</p>
              </div>
            )}
          </EditableSection>
        </div>
      </section>

      {/* Immigration Types */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <EditableSection sectionKey="immigration.types" data={sections['immigration.types']} fallback={typesFallback} onSaved={refetch}>
            {(content) => {
              const currentTab = activeTab || content.items[0]?.id;
              return (
                <>
                  <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.title}</h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">{content.subtitle}</p>
                  </div>

                  <div className="grid-centered md:[--cols:2] lg:[--cols:4] [--gap:1.5rem]">
                    {content.items.map((item) => {
                      const Icon = resolveIcon(item.icon);
                      return (
                        <button
                          key={item.id}
                          onClick={() => setActiveTab(item.id)}
                          className={`card text-center group hover:-translate-y-2 transition-all duration-300 ${
                            currentTab === item.id ? 'ring-2 ring-primary-500 bg-primary-50' : 'hover:shadow-xl'
                          }`}
                        >
                          <div
                            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors duration-300 ${
                              currentTab === item.id
                                ? 'bg-primary-600 text-white'
                                : 'bg-primary-100 text-primary-600 group-hover:bg-primary-600 group-hover:text-white'
                            }`}
                          >
                            <Icon className="w-8 h-8" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                          <p className="text-gray-600">{item.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </>
              );
            }}
          </EditableSection>
        </div>
      </section>

      {/* Countries Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <EditableSection sectionKey="immigration.countries" data={sections['immigration.countries']} fallback={countriesFallback} onSaved={refetch}>
            {(content) => (
              <>
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.title}</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">{content.subtitle}</p>
                </div>

                <div className="grid-centered md:[--cols:2]">
                  {content.items.map((item) => (
                    <div key={item.id} className="card group hover:-translate-y-2 transition-transform duration-300">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="text-4xl">{item.flag}</div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{item.title}</h3>
                          <p className="text-gray-600">{item.description}</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">{t('immigration.popularVisas')}</h4>
                          <div className="flex flex-wrap gap-2">
                            {item.popularVisas.map((visa, visaIndex) => (
                              <span key={visaIndex} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                                {visa}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">{t('immigration.requirements')}</h4>
                          <ul className="space-y-1">
                            {item.requirements.map((req, reqIndex) => (
                              <li key={reqIndex} className="flex items-center space-x-2 text-gray-600">
                                <CheckCircle className="w-4 h-4 text-secondary-500 flex-shrink-0" />
                                <span>{req}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </EditableSection>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <EditableSection sectionKey="immigration.process" data={sections['immigration.process']} fallback={processFallback} onSaved={refetch}>
            {(content) => (
              <>
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.title}</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">{content.subtitle}</p>

                  <div className="max-w-2xl mx-auto mb-12">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-gray-900">{t('immigration.progressLabel')}</span>
                      <span className="text-lg font-bold text-primary-600">100%</span>
                    </div>
                    <AnimatedProgressBar
                      progress={100}
                      className="w-full"
                      showPercentage={false}
                      duration={3000}
                      barClassName="h-4"
                    />
                    <p className="text-sm text-gray-600 mt-2">{t('immigration.avgDuration')}</p>
                  </div>
                </div>

                <div className="grid-centered md:[--cols:2] lg:[--cols:3]">
                  {content.items.map((item, index) => {
                    const Icon = resolveIcon(item.icon);
                    const progressPercentage = content.items.length > 1 ? (index / (content.items.length - 1)) * 100 : 100;
                    return (
                      <div
                        key={item.id}
                        className="card text-center group hover:-translate-y-2 transition-all duration-500 hover:shadow-xl hover:shadow-primary-100"
                        style={{
                          animation: `fadeInUp 0.8s ease-out ${index * 0.2}s both`,
                          transform: 'translateY(20px)',
                          opacity: 0,
                        }}
                      >
                        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-600 transition-colors duration-300">
                          <Icon className="w-8 h-8 text-primary-600 group-hover:text-white transition-colors duration-300" />
                        </div>
                        <div className="text-2xl font-bold text-primary-600 mb-2">
                          {t('immigration.step', { n: index + 1 })}
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                        <p className="text-gray-600 mb-4">{item.description}</p>

                        <div className="mb-4">
                          <AnimatedProgressBar
                            progress={progressPercentage}
                            className="w-full"
                            showPercentage={true}
                            duration={1500}
                          />
                        </div>

                        <div className="text-sm text-gray-500 bg-gray-100 rounded-full px-3 py-1 inline-block">
                          {item.duration}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </EditableSection>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <EditableSection sectionKey="immigration.services" data={sections['immigration.services']} fallback={servicesFallback} onSaved={refetch}>
            {(content) => (
              <>
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.title}</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">{content.subtitle}</p>
                </div>

                <div className="grid-centered md:[--cols:3]">
                  {content.items.map((item) => (
                    <div key={item.id} className="card group hover:-translate-y-2 transition-transform duration-300">
                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                        <p className="text-gray-600 mb-6">{item.description}</p>
                      </div>

                      <ul className="space-y-3 mb-8">
                        {item.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center space-x-3">
                            <CheckCircle className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="text-center">
                        <Button className="w-full"
                          href="/contato">
                          {t('immigration.requestQuote')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </EditableSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6">{t('immigration.ctaTitle')}</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">{t('immigration.ctaSubtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contato"
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              {t('immigration.freeConsult')}
            </a>
            <a
              href="/blog"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-all duration-200"
            >
              {t('immigration.learnMore')}
            </a>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Immigration;